import { FlexRow } from '@/components/common/Flex';
import { Grid } from '@chakra-ui/react';
import {
    ExternalFinancialEntity,
    ExternalFinancialEntityType,
} from '@common/financialEntities';
import { useAddAccountManual } from '@common/react/accounts/useAddAccountManual';
import { TOASTS } from '@common/toasts';
import { FC } from 'react';
import { Button } from '../common/Button';
import { FlexCol } from '../common/Flex';
import Form from '../common/Form';
import InputField from '../common/InputField';
import { useErrorToast } from '../common/Toasts';

export interface ManualAddFormProps {
    type: ExternalFinancialEntityType;
    nextButtonText: string;
    onComplete?: (entity: ExternalFinancialEntity) => Promise<void>;
    onCancel?: () => void;
}

const ManualAddForm: FC<ManualAddFormProps> = ({
    type,
    nextButtonText,
    onComplete,
    onCancel,
}) => {
    const showErrorToast = useErrorToast();

    const { addForm, title, form, changeField, submit, isSubmitting, invalid } =
        useAddAccountManual({
            type,
            onComplete,
        });

    const submitWithErrorHandling = () => {
        submit().catch(() => {
            showErrorToast(TOASTS.manualAddAccountError);
        });
    };

    return (
        <Form
            disabled={isSubmitting}
            onSubmit={submitWithErrorHandling}
            opacity={isSubmitting ? 0.5 : 1}
            flexGrow={1}
        >
            <FlexCol h="full">
                <Grid templateColumns={['1fr', 'repeat(2, 1fr)']} gap={8}>
                    {addForm.fields
                        .filter((field) => field.isVisible?.(form) !== false)
                        .map((field, i) => (
                            <InputField
                                key={field.name}
                                tabIndex={i + 1}
                                {...field}
                                value={form[field.name]}
                                placeholder={
                                    field.name === 'name' && !field.isRequired
                                        ? title
                                        : undefined
                                }
                                onChange={(value) =>
                                    changeField(field.name, value)
                                }
                                inputProps={{
                                    size: 'lg',
                                }}
                            />
                        ))}
                </Grid>
                <FlexRow grow={1} align="end" justify="space-between">
                    {onCancel && (
                        <Button
                            variant="ghost"
                            colorScheme="gray"
                            onClick={onCancel}
                            onClickEvent="Manual Add Cancel Button Clicked"
                            tabIndex={99}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        disabled={invalid || isSubmitting}
                        isLoading={isSubmitting}
                        type="submit"
                        tabIndex={98}
                        onClickEvent="Manual Add Submit Button Clicked"
                    >
                        {nextButtonText}
                    </Button>
                </FlexRow>
            </FlexCol>
        </Form>
    );
};

export default ManualAddForm;
